import {IconActivity, IconX, IconArrowBackUp, IconPlus, IconDeviceFloppy} from "@tabler/icons-react";
import {Button, ColorInput, Input, NumberInput, Textarea, Space, Table, Flex} from "@mantine/core";
import React, {useEffect, useState} from "react";
import {TableEditorRowModel} from "../models/tableEditor/TableEditorRowModel";
import {TableEditorModel} from "../models/tableEditor/TableEditorModel";

/**
 * TableEditor
 * @param body TableEditorModel
 */
export function TableEditor<T>(body: TableEditorModel){
    const headers = body.headers;

    // Initialize liveRows
    const rows = useState<string[][]>([]);
    const liveRows = useState<TableEditorRowModel[]>([]);

    /**
     * Function to get the rows
     */
    async function getRows() {
        const bodyData = await body.apiObject.Execute<T>(body.getURL, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json'
            }
        });

        // Check if the data is an array
        if (Array.isArray(bodyData)) {
            // Empty the liveRows and rows
            liveRows[0] = [];
            rows[0] = [];

            // Add each row to the rows
            bodyData.forEach((row) => {
                rows[0].push(Object.keys(row).map((key) => {
                    return row[key].toString();
                }));
                rows[1]([...rows[0]]);

                // Initialize liveRows
                liveRows[0].push({
                    elementID: crypto.randomUUID(),
                    content: [...rows[0][rows[0].length - 1]],
                    status: null
                })
                liveRows[1]([...liveRows[0]]);
            });
        }
    }

    // Initialize the initial rows
    useEffect(() => {
        getRows();
    }, []);

    /**
     * Function to handle the change of a row
     * @param elementID The element ID of the row
     * @param index The index of the row
     * @param value The new value of the row
     */
    function onChangeRow(elementID: string, index: number, value: string) {
        const rowIndex = liveRows[0].findIndex((x) => x.elementID === elementID);
        liveRows[0][rowIndex].content[index] = value;

        // If the status is not post or del then set the status to put
        if (liveRows[0][rowIndex].status !== 'post' && liveRows[0][rowIndex].status !== 'del') {
            // Compare the row to the original row, if it is the same then set the status to null
            if (liveRows[0][rowIndex].content.toString() === rows[0][rowIndex].toString()) {
                liveRows[0][rowIndex].status = null;
            }
            // If the row is different from the original row, set the status to put
            else {
                liveRows[0][rowIndex].status = 'put';
            }
        }

        liveRows[1]([...liveRows[0]]);
    }

    /**
     * Function to add a row
     */
    function onAddRow() {
        liveRows[0].push({
            elementID: crypto.randomUUID(),
            content: new Array(headers.length).fill(''),
            status: 'post'
        });
        // Set any view only fields to 'TBD'
        liveRows[0][liveRows[0].length - 1].content = liveRows[0][liveRows[0].length - 1].content.map((element, index) => {
            return headers[index].viewOnly ? 'TBD' : element;
        });
        liveRows[1]([...liveRows[0]]);
    }

    /**
     * Function to delete a row
     * @param elementID The element ID of the row
     */
    function onDelRow(elementID: string) {
        const index = liveRows[0].findIndex((x) => x.elementID === elementID);

        // Check if the row is already deleted
        if (liveRows[0][index].status === 'del') {
            // Compare the row to the original row, if it is the same then set the status to null
            if (liveRows[0][index].content.toString() === rows[0][index].toString()) {
                liveRows[0][index].status = null;
                liveRows[1]([...liveRows[0]]);
            }
            // If the row is different from the original row, set the status to put
            else {
                liveRows[0][index].status = 'put';
                liveRows[1]([...liveRows[0]]);
            }
        }
        // Check if the row is new
        else if (liveRows[0][index].status === 'post') {
            // Remove the row from the liveRows
            liveRows[0].splice(index, 1);
            liveRows[1]([...liveRows[0]]);
        }
        else {
            // Set the status to del, as the row is not new
            liveRows[0][index].status = 'del';
            liveRows[1]([...liveRows[0]]);
        }
    }

    /**
     * Function to render a row
     * @param row The row to render
     */
    function renderRow(row: TableEditorRowModel) {
        return (
            <tr key={row.elementID} style=
                {row.status === 'del' ? {background: 'rgba(224, 49, 49,0.25)'}:
                row.status === 'post' ? {background: 'rgba(47, 158, 68,0.25)'}:
                row.status === 'put' ? {background: 'rgba(25, 113, 194,0.25)'}: {}
            }>
                {row.content.map((element, index) => {
                    return renderElement(row.elementID, element, row.status === 'post', index);
                })}
                <td>
                    <Button color={row.status === 'del' ? 'blue': 'red'} onClick={() => onDelRow(row.elementID)} disabled={body.delURL === undefined && row.status !== 'post'}>
                        {row.status === 'del' ? <IconArrowBackUp stroke={1}/> : <IconX stroke={1}/>}
                    </Button>
                </td>
            </tr>
        );
    }

    /**
     * Function to render an element
     * @param elementID The element ID of the row
     * @param element The element to render
     * @param isNew If the element is local or not
     * @param index The index of the element
     */
    function renderElement(elementID: string, element: string, isNew: boolean, index: number) {
        // Check if the element is view only
        if (headers[index].viewOnly || (body.putURL === undefined && !isNew)) {
            // Render the element
            return (
                <td>
                    {element}
                </td>
            );
        }
        // If the element is not view only then render an input
        else {
            // Check the type of the element
            switch (headers[index].type) {
                // If the element is a rich text
                case 'rich':
                    return (
                        <td>
                            <Textarea px={'sm'} defaultValue={element} onChange={(x) => onChangeRow(
                                elementID,
                                index,
                                x.target.value
                            )}/>
                        </td>
                    );
                // If the element is a number
                case 'number':
                    return (
                        <td>
                            <NumberInput px={'sm'} defaultValue={Number(element)} onChange={(x) => onChangeRow(
                                elementID,
                                index,
                                x.toString()
                            )}/>
                        </td>
                    );
                // If the element is a colour
                case 'colour':
                    return (
                        <td>
                            <ColorInput px={'sm'} defaultValue={'#' + (element.replace('#', ''))} onChangeEnd={(x) => onChangeRow(
                                elementID,
                                index,
                                x.toString()
                            )}/>
                        </td>
                    );
                // If the element is not recognised, default to text
                default:
                    return (
                        <td>
                            <Input px={'sm'} defaultValue={element} onChange={(x) => onChangeRow(
                                elementID,
                                index,
                                x.target.value
                            )}/>
                        </td>
                    );
            }
        }
    }

    /**
     * Function to build a JSON object from a row
     * @param row The row to build the JSON object from
     */
    function buildJson(row: TableEditorRowModel, isPost: boolean = false) {
        let json: { [key: string]: any } = {};

        for (let i = 0; i < headers.length; i++) {
            if (isPost) {
                // Check if it's undefined
                if (body.postURL === undefined) {
                    break;
                }
                // Build the JSON object
                if (body.postURL.keys.includes(headers[i].key)) {
                    json[headers[i].key] = row.content[i];
                }
            }
            else {
                // Check if it's undefined
                if (body.putURL === undefined) {
                    break;
                }
                // Build the JSON object
                if (body.putURL.keys.includes(headers[i].key)) {
                    json[headers[i].key] = row.content[i];
                }
            }
        }

        return json
    }

    /**
     * Function to save the changes on the table
     */
    async function saveTable() {
        // Loop through the rows
        let error = false;
        for (let i = 0; i < liveRows[0].length; i++) {
            // Check the status of the row
            switch (liveRows[0][i].status) {
                // If the row is up for deletion
                case 'del':
                    await body.apiObject.ExecuteWithoutBody(body.delURL + '/' + liveRows[0][i].content[0], {
                        method: 'DELETE',
                        headers: {
                            'Content-Type': 'application/json'
                        }
                    })
                    break;
                // If the row is up for posting
                case 'post':
                    await body.apiObject.ExecuteWithoutBody(body.postURL?.url + '', {
                        method: 'POST',
                        body: JSON.stringify(buildJson(liveRows[0][i], true)),
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    })
                    break;
                // If the row is up for updating
                case 'put':
                    await body.apiObject.ExecuteWithoutBody(body.putURL?.url + '/' + liveRows[0][i].content[0], {
                        method: 'PUT',
                        body: JSON.stringify(buildJson(liveRows[0][i])),
                        headers: {
                            'Accept': 'application/json',
                            'Content-Type': 'application/json'
                        }
                    })
                    break;
                default:
                    break;
            }

            // Set the status of the row to null
            liveRows[0][i].status = null;
            liveRows[1]([...liveRows[0]]);
        }

        // Refresh the rows, if there was no error
        if (!error) {
            await getRows();
        }
    }

    return (
        <>
            <Table highlightOnHover withColumnBorders>
                <thead>
                <tr>
                    {headers.map((header) => {
                        return (
                            <th>
                                {header.icon !== null && header.icon} {header.title}
                            </th>
                        );
                    })}
                    <th><IconActivity size="0.8rem"/> Action</th>
                </tr>
                </thead>
                <tbody>
                {liveRows[0].map((row) => {
                    return renderRow(row);
                })}
                </tbody>
            </Table>
            <Space h={'md'} />
            <Flex gap={'sm'}>
                {body.postURL !== undefined && <Button color="gray" onClick={() => {onAddRow()}}><IconPlus stroke={1}/></Button>}
                {(body.postURL !== undefined || body.putURL !== undefined || body.delURL !== undefined)
                && <Button color="gray" onClick={() => {saveTable()}}><IconDeviceFloppy stroke={1} /></Button>}
            </Flex>
        </>
    );
}